import { MAX_ZONES_PER_CONDENSER, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  ConfigurationType,
  GridSpaceDataType,
  LevelType,
  LineSetType
} from 'data/types';
import { ZoneConcealedDuctLayout } from 'utils/concealed-duct-geometry';
import { findLevelForSpace } from 'utils/grid';
import { buildLinesetForPair } from 'utils/linesets';

const AH_GRID_HALF_SQUARES = 1.5;

function isConcealedDuctType(
  typeCode: AirHandlerTypeCodeType | undefined
): boolean {
  return typeCode === 'concealed' || typeCode === 'concealed_high_static';
}

export type CondenserDescriptor = {
  uniqueId: string;
  levelUniqueId: string | undefined;
  position: { x: number; y: number };
};

export type AirHandlerDescriptor = {
  uniqueId: string;
  levelUniqueId: string | undefined;
  position: { x: number; y: number };
  targetSpaceId: string | undefined;
  airHandlerTypeCode?: AirHandlerTypeCodeType;
  concealedLocation?: ConcealedDuctLocationType | null;
  condenserUniqueId: string;
};

export type CondenserAirHandlerPair = {
  condenserUniqueId: string;
  airHandlerUniqueId: string;
};

function getCondenserCenter(c: CondenserDescriptor): { x: number; y: number } {
  return {
    x: c.position.x + 2,
    y: c.position.y + 2
  };
}

function getAirHandlerCenter(a: AirHandlerDescriptor): {
  x: number;
  y: number;
} {
  return {
    x: a.position.x + 2,
    y: a.position.y + 2
  };
}

function distance(
  a: { x: number; y: number },
  b: { x: number; y: number }
): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function getVisibleCondensers(
  config: ConfigurationType
): CondenserDescriptor[] {
  return config.condensers
    .filter((c) => c.position !== undefined)
    .map((c) => ({
      uniqueId: c.uniqueId,
      levelUniqueId: c.levelUniqueId,
      position: { x: c.position!.x, y: c.position!.y }
    }));
}

export function getAirHandlersFromConfig(
  config: ConfigurationType,
  structure: LevelType[],
  concealedDuctLayouts?: Map<string, ZoneConcealedDuctLayout>
): AirHandlerDescriptor[] {
  const result: AirHandlerDescriptor[] = [];
  config.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      const { zoneItems, airHandler } = zone;
      if (zoneItems.length > 0 && airHandler) {
        const { structureItemId } = zoneItems[0];
        const layout = concealedDuctLayouts?.get(zone.uniqueId);
        const useEffectiveCdPosition =
          !!layout &&
          isConcealedDuctType(airHandler.airHandlerTypeCode) &&
          !airHandler.userPositioned;
        let position: { x: number; y: number };
        let levelUniqueId: string | undefined;
        if (useEffectiveCdPosition && layout) {
          position = {
            x: layout.originPx.x / STRUCTURE_GRID_SIZE - AH_GRID_HALF_SQUARES,
            y: layout.originPx.y / STRUCTURE_GRID_SIZE - AH_GRID_HALF_SQUARES
          };
          levelUniqueId = structure[layout.originLevelIndex]?.uniqueId;
        } else {
          position = {
            x: airHandler.positionX,
            y: airHandler.positionY
          };
          levelUniqueId = findLevelForSpace(
            structureItemId,
            structure
          )?.uniqueId;
        }
        result.push({
          uniqueId: airHandler.uniqueId,
          levelUniqueId,
          position,
          targetSpaceId: structureItemId,
          airHandlerTypeCode: airHandler.airHandlerTypeCode,
          concealedLocation: airHandler.concealedLocation ?? null,
          condenserUniqueId: condenser.uniqueId
        });
      }
    });
  });
  return result;
}

export function rebuildLinesetsForCondenser(
  config: ConfigurationType,
  condenserUniqueId: string,
  structure: LevelType[],
  getGridSpacesForLevel: (levelIndex: number) => GridSpaceDataType[],
  concealedDuctLayouts?: Map<string, ZoneConcealedDuctLayout>
): LineSetType[] {
  const lineSets = config.lineSets ?? [];
  const condenser = config.condensers.find(
    (c) => c.uniqueId === condenserUniqueId
  );
  if (!condenser?.position) return lineSets;

  const airHandlers = getAirHandlersFromConfig(
    config,
    structure,
    concealedDuctLayouts
  );
  const condenserDescriptor = {
    uniqueId: condenser.uniqueId,
    levelUniqueId: condenser.levelUniqueId,
    position: { x: condenser.position.x, y: condenser.position.y }
  };

  return lineSets.map((lineset) => {
    if (lineset.condenserUniqueId !== condenserUniqueId) return lineset;
    const airHandler = airHandlers.find(
      (ah) => ah.uniqueId === lineset.airHandlerUniqueId
    );
    if (!airHandler) return lineset;
    return buildLinesetForPair(
      condenserDescriptor,
      airHandler,
      structure,
      getGridSpacesForLevel,
      config.uniqueId
    );
  });
}

export function computeCondenserAirHandlerAssignment(
  config: ConfigurationType,
  structure: LevelType[],
  concealedDuctLayouts?: Map<string, ZoneConcealedDuctLayout>
): CondenserAirHandlerPair[] {
  const condensers = getVisibleCondensers(config);
  const airHandlers = getAirHandlersFromConfig(
    config,
    structure,
    concealedDuctLayouts
  );
  if (condensers.length === 0 || airHandlers.length === 0) {
    return [];
  }

  const capacity = new Map<string, number>();
  condensers.forEach((c) => capacity.set(c.uniqueId, MAX_ZONES_PER_CONDENSER));
  const pairs: CondenserAirHandlerPair[] = [];
  const assignedAhIds = new Set<string>();

  const visibleCondenserIds = new Set(condensers.map((c) => c.uniqueId));
  airHandlers.forEach((ah) => {
    if (visibleCondenserIds.has(ah.condenserUniqueId)) {
      pairs.push({
        condenserUniqueId: ah.condenserUniqueId,
        airHandlerUniqueId: ah.uniqueId
      });
      assignedAhIds.add(ah.uniqueId);
      const cap = capacity.get(ah.condenserUniqueId) ?? 0;
      capacity.set(ah.condenserUniqueId, cap - 1);
    }
  });

  const assignToClosestOnLevel = (
    ah: AirHandlerDescriptor,
    levelUniqueId: string | undefined
  ): boolean => {
    const condensersOnLevel = condensers.filter(
      (c) =>
        c.levelUniqueId === levelUniqueId && (capacity.get(c.uniqueId) ?? 0) > 0
    );
    if (condensersOnLevel.length === 0) return false;
    const ahCenter = getAirHandlerCenter(ah);
    const sorted = [...condensersOnLevel].sort((a, b) => {
      const da = distance(ahCenter, getCondenserCenter(a));
      const db = distance(ahCenter, getCondenserCenter(b));
      return da - db;
    });
    const chosen = sorted[0];
    const cap = capacity.get(chosen.uniqueId)!;
    capacity.set(chosen.uniqueId, cap - 1);
    pairs.push({
      condenserUniqueId: chosen.uniqueId,
      airHandlerUniqueId: ah.uniqueId
    });
    assignedAhIds.add(ah.uniqueId);
    return true;
  };

  const levelsWithCondensers = new Set(
    condensers.map((c) => c.levelUniqueId).filter(Boolean)
  ) as Set<string>;

  levelsWithCondensers.forEach((levelUniqueId) => {
    const ahsOnLevel = airHandlers.filter(
      (ah) =>
        ah.levelUniqueId === levelUniqueId && !assignedAhIds.has(ah.uniqueId)
    );
    ahsOnLevel.forEach((ah) => assignToClosestOnLevel(ah, levelUniqueId));
  });

  const remaining = airHandlers.filter((ah) => !assignedAhIds.has(ah.uniqueId));
  remaining.forEach((ah) => {
    const ahCenter = getAirHandlerCenter(ah);
    const withCapacity = condensers.filter(
      (c) => (capacity.get(c.uniqueId) ?? 0) > 0
    );
    if (withCapacity.length === 0) return;
    const sorted = [...withCapacity].sort((a, b) => {
      const da = distance(ahCenter, getCondenserCenter(a));
      const db = distance(ahCenter, getCondenserCenter(b));
      return da - db;
    });
    const chosen = sorted[0];
    const cap = capacity.get(chosen.uniqueId)!;
    capacity.set(chosen.uniqueId, cap - 1);
    pairs.push({
      condenserUniqueId: chosen.uniqueId,
      airHandlerUniqueId: ah.uniqueId
    });
    assignedAhIds.add(ah.uniqueId);
  });

  return pairs;
}
